import { ReactNode } from "react";
import { PortableText, PortableTextProps, PortableTextReactComponents } from "next-sanity";
import LinkSolstice from "../LinkSolstice/LinkSolstice";
import { RichTextDownload } from "../../rich-text/RichTextDownload/RichTextDownload";
import { Highlight } from "../Highlight/Highlight";
import styles from "./PortableTextRichText.module.scss";

interface PortableTextRichTextProps {
	value: PortableTextProps["value"];
	asFragment?: boolean;
}

export const PortableTextRichText = ({ value, asFragment }: PortableTextRichTextProps) => {
	const components = { ...portableTextComponents };

	if (asFragment) {
		components.block = ({ children }: { children?: ReactNode }) => <>{children}</>;
	}

	return (
		<PortableText
			value={value}
			components={components}
		/>
	);
};

export const portableTextComponents: Partial<PortableTextReactComponents> = {
	marks: {
		disclaimerReference: ({ children }) => <>{children}</>,
		link: ({ children, value }) => {
			const rel = !value?.href?.startsWith("/") ? "noreferrer noopener" : undefined;
			return (
				<LinkSolstice
					href={value?.href ?? "#"}
					className={styles["link"]}
					rel={rel}
					target={value?.openInNewTab ? "_blank" : undefined}
				>
					{children}
				</LinkSolstice>
			);
		},
		highlight: ({ children }) => <Highlight>{children}</Highlight>,
	},
	types: {
		disclaimerMarker: ({ value }) => (
			<sup aria-label={`Disclaimer ${value.number}`}>
				{value.marker}
			</sup>
		),
		download: ({ value }) => (
			<RichTextDownload
				file={value.download}
				label={value.title}
			/>
		),
	},
	block: {
		h3: ({ children }) => <h3 className={styles["h3"]}>{children}</h3>,
	},

	list: {
		bullet: ({ children }) => <ul className={styles["ul"]}>{children}</ul>,
		number: ({ children }) => <ol className={styles["ol"]}>{children}</ol>,
	},
	unknownBlockStyle: v => {
		console.error("PortableText Error: Unknown block style", v);
		return null;
	},
	unknownMark: v => {
		console.error("PortableText Error: Unknown mark", v);
		return null;
	},
	unknownList: v => {
		console.error("PortableText Error: Unknown list", v);
		return null;
	},
	unknownListItem: v => {
		console.error("PortableText Error: Unknown list item", v);
		return null;
	},
	unknownType: v => {
		console.error("PortableText Error: Unknown type", v);
		return null;
	},
};
